import { config } from '@abyss/web/tools/config';
import { SSOPortalEligibilityData } from 'api/src/dataModels/SSOPortalEligibilityData';
import { PortalIdentifier } from 'api/src/dataModels/PortalDataEnums';
import { PlanNetworkIndicators } from 'api/src/dataModels/PortalEligibilityData';

interface EligibilityCheckResult {
  isUmrLeased?: boolean;
  planNetworkIndicators?: PlanNetworkIndicators;
}

const CHECK_ELIGIBILITY_QUERY = `
  query checkUmrLeasedEligibility(
    $firstName: String!
    $lastName: String!
    $memberId: String!
    $grpNumber: String!
    $mbrBirthDt: String!
  ) {
    checkUmrLeasedEligibility(
      firstName: $firstName
      lastName: $lastName
      memberId: $memberId
      grpNumber: $grpNumber
      mbrBirthDt: $mbrBirthDt
    ) {
      isUmrLeased
      planNetworkIndicators {
        medicalInd
        behavioralInd
        dentalInd
        visionInd
      }
    }
  }
`;

const hasEligibilityCheckInputs = (data: SSOPortalEligibilityData): boolean => {
  return Boolean(
    data.firstName &&
    data.lastName &&
    data.memberId &&
    data.policyId &&
    data.dob
  );
};

export const applyUmrLeasedIdentifier = async (
  data: SSOPortalEligibilityData
): Promise<SSOPortalEligibilityData> => {
  if (!hasEligibilityCheckInputs(data)) {
    return data;
  }

  const gqlUrl = config('CEX_GRAPHQL_API_URL');
  if (!gqlUrl) {
    return data;
  }

  const operationName = 'checkUmrLeasedEligibility';

  try {
    const response = await fetch(`${gqlUrl}?q=${operationName}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        operationName,
        query: CHECK_ELIGIBILITY_QUERY,
        variables: {
          firstName: data.firstName,
          lastName: data.lastName,
          memberId: data.memberId,
          grpNumber: data.policyId,
          mbrBirthDt: data.dob.replace(/-/g, ''), // "1974-12-03" → "19741203"
        },
      }),
    });

    if (!response.ok) {
      return data;
    }

    const json = await response.json();
    const result = json?.data
      ?.checkUmrLeasedEligibility as EligibilityCheckResult;
    if (!result?.isUmrLeased) {
      return data;
    }

    return {
      ...data,
      portalIdentifier: PortalIdentifier.UMR_LEASED,
      ...(result.planNetworkIndicators
        ? { planNetworkIndicators: result.planNetworkIndicators }
        : {}),
    };
  } catch {
    // Non-blocking: if eligibility check fails, proceed with default portalIdentifier.
    return data;
  }
};
